import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, ShieldCheck, KeyRound, UserPlus, Trash2, Copy, Check, ScrollText, X, MapPin, Plus, Wand2, Circle, Pencil } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { categoryLabel } from "@/lib/tournament";
import api, { formatApiError } from "@/lib/api";

const inputCls = "rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400";
const isReal = (x) => x && x !== "BYE";

function fmtTime(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch (e) { return iso; }
}

export default function Umpires({ tournament, onUpdate }) {
  const tid = tournament.tournament_id;
  const cats = tournament.categories || [];
  const courts = tournament.courts || [];
  const [courtInput, setCourtInput] = useState("");
  const [courtBusy, setCourtBusy] = useState(false);
  const [overview, setOverview] = useState(null);
  const [assignBusy, setAssignBusy] = useState(false);
  const [umpires, setUmpires] = useState([]);
  const [pins, setPins] = useState([]);
  const [acct, setAcct] = useState({ name: "", email: "" });
  const [creds, setCreds] = useState(null);
  const [pinCat, setPinCat] = useState(cats[0]?.id || "");
  const [pinMatch, setPinMatch] = useState("");
  const [pinLabel, setPinLabel] = useState("");
  const [matches, setMatches] = useState([]);
  const [newPin, setNewPin] = useState(null);
  const [copied, setCopied] = useState(false);
  const [auditOpen, setAuditOpen] = useState(false);
  const [audit, setAudit] = useState(null);
  const [auditMatch, setAuditMatch] = useState(null);
  const [editAcct, setEditAcct] = useState(null);
  const [busy, setBusy] = useState(false);

  const saveAcct = async () => {
    if (!editAcct) return;
    const body = { name: editAcct.name, email: editAcct.email };
    if (editAcct.password) body.password = editAcct.password;
    try {
      await api.patch(`/tournaments/${tid}/umpires/${editAcct.user_id}`, body);
      toast.success(editAcct.password ? "Account & password updated" : "Account updated");
      setEditAcct(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Update failed"); }
  };

  const load = useCallback(() => {
    api.get(`/tournaments/${tid}/umpires`).then(({ data }) => setUmpires(data)).catch(() => {});
    api.get(`/tournaments/${tid}/umpire-pins`).then(({ data }) => setPins(data)).catch(() => {});
  }, [tid]);
  useEffect(() => { load(); }, [load]);

  const loadOverview = useCallback(() => {
    api.get(`/tournaments/${tid}/courts/overview`).then(({ data }) => setOverview(data)).catch(() => setOverview(null));
  }, [tid]);
  useEffect(() => {
    if (courts.length === 0) { setOverview(null); return; }
    loadOverview();
    const timer = setInterval(loadOverview, 4000);
    return () => clearInterval(timer);
  }, [loadOverview, courts.length]);

  const autoAssign = async () => {
    setAssignBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${tid}/courts/auto-assign`);
      if (data.assigned > 0) toast.success(`Assigned ${data.assigned} court${data.assigned > 1 ? "s" : ""}${data.remaining_unassigned > 0 ? ` · ${data.remaining_unassigned} match(es) still waiting for a free court` : ""}`);
      else toast.info(data.free_courts === 0 ? "All courts are busy right now" : "No ready matches need a court");
      loadOverview();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not auto-assign"); }
    finally { setAssignBusy(false); }
  };

  useEffect(() => {
    if (!pinCat) { setMatches([]); return; }
    api.get(`/tournaments/${tid}/categories/${pinCat}/bracket`)
      .then(({ data }) => setMatches(data.matches.filter((m) => isReal(m.a_name) && isReal(m.b_name))))
      .catch(() => setMatches([]));
  }, [tid, pinCat]);

  const createAccount = async () => {
    if (!acct.name.trim() || !acct.email.trim()) { toast.error("Name and email required"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${tid}/umpires`, acct);
      setCreds(data);
      setAcct({ name: "", email: "" });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not create"); }
    finally { setBusy(false); }
  };

  const removeUmpire = async (uid) => {
    try { await api.delete(`/tournaments/${tid}/umpires/${uid}`); load(); toast.success("Umpire unassigned"); }
    catch (e) { toast.error("Failed"); }
  };

  const genPin = async () => {
    if (!pinCat || !pinMatch || !pinLabel.trim()) { toast.error("Pick a category, match and label"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/tournaments/${tid}/umpire-pins`, { label: pinLabel.trim(), category_id: pinCat, match_id: pinMatch });
      setNewPin(data);
      setPinLabel(""); setPinMatch("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not generate PIN"); }
    finally { setBusy(false); }
  };

  const revokePin = async (pid) => {
    try { await api.delete(`/tournaments/${tid}/umpire-pins/${pid}`); load(); toast.success("PIN revoked"); }
    catch (e) { toast.error("Failed"); }
  };

  const openAudit = async () => {
    setAuditOpen(true); setAudit(null); setAuditMatch(null);
    try { const { data } = await api.get(`/tournaments/${tid}/audit`); setAudit(data); }
    catch (e) { setAudit(false); }
  };

  const copyPin = () => { navigator.clipboard?.writeText(newPin.pin); setCopied(true); toast.success("PIN copied"); };

  const addCourt = async () => {
    const name = courtInput.trim();
    if (!name) return;
    if (courts.some((c) => c.toLowerCase() === name.toLowerCase())) { toast.error("That court already exists"); return; }
    if (!onUpdate) return;
    setCourtBusy(true);
    try { await onUpdate({ courts: [...courts, name] }, true); setCourtInput(""); }
    finally { setCourtBusy(false); }
  };

  const removeCourt = async (name) => {
    if (!onUpdate) return;
    setCourtBusy(true);
    try { await onUpdate({ courts: courts.filter((c) => c !== name) }, true); }
    finally { setCourtBusy(false); }
  };

  return (
    <div className="rounded-2xl glow-border p-5" style={{ background: "var(--surface)" }} data-testid="umpires-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="font-display font-bold flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-primary-c" /> Umpires & Scoring</h3>
        <button data-testid="open-audit" onClick={openAudit} className="glass rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5" style={{ color: "var(--text)" }}><ScrollText className="w-3.5 h-3.5 text-primary-c" /> Audit log</button>
      </div>

      {/* Courts */}
      <div className="rounded-xl p-3 mb-5" style={{ background: "var(--bg)" }} data-testid="courts-manager">
        <div className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--text-2)" }}><MapPin className="w-3.5 h-3.5 text-primary-c" /> Courts</div>
        <p className="text-[11px] mb-2" style={{ color: "var(--text-2)" }}>Name your courts once, then assign them to matches from the bracket. They show on the live bracket and umpire console.</p>
        <div className="flex gap-2 mb-2">
          <input data-testid="court-input" value={courtInput} onChange={(e) => setCourtInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCourt(); } }}
            placeholder="e.g. Court 1, Center Court" className={`${inputCls} flex-1`} style={{ color: "var(--text)" }} />
          <button data-testid="add-court" onClick={addCourt} disabled={courtBusy || !courtInput.trim()} className="btn-glow rounded-xl px-4 py-2.5 text-sm font-bold flex items-center gap-1.5 disabled:opacity-60"><Plus className="w-4 h-4" /> Add</button>
        </div>
        <div className="flex flex-wrap gap-2" data-testid="court-list">
          {courts.length === 0 ? <p className="text-xs" style={{ color: "var(--text-2)" }}>No courts yet.</p> :
            courts.map((c) => (
              <span key={c} data-testid={`court-chip-${c}`} className="glass rounded-full pl-3 pr-1.5 py-1 text-xs font-bold flex items-center gap-1.5">
                <MapPin className="w-3 h-3 text-primary-c" /> {c}
                <button data-testid={`remove-court-${c}`} onClick={() => removeCourt(c)} disabled={courtBusy} className="w-5 h-5 rounded-full glass flex items-center justify-center"><X className="w-3 h-3" style={{ color: "#ef4444" }} /></button>
              </span>
            ))}
        </div>

        {courts.length > 0 && (
          <div className="mt-3 pt-3 border-t" style={{ borderColor: "var(--border)" }} data-testid="court-status">
            <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
              <div className="text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5" style={{ color: "var(--text-2)" }}>
                Court status
                {overview && <span className="mono normal-case" style={{ color: "var(--text-2)" }}>· {overview.courts.filter((c) => !c.in_use).length} free / {overview.courts.length}</span>}
              </div>
              <button data-testid="auto-assign-courts" onClick={autoAssign} disabled={assignBusy || !overview || overview.ready_unassigned.length === 0}
                className="btn-glow rounded-full px-3 py-1.5 text-xs font-bold flex items-center gap-1.5 disabled:opacity-50"
                title={overview && overview.ready_unassigned.length === 0 ? "No ready matches waiting for a court" : ""}>
                {assignBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Auto-assign
                {overview && overview.ready_unassigned.length > 0 ? ` (${overview.ready_unassigned.length})` : ""}
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5" data-testid="court-status-list">
              {(overview?.courts || []).map((c) => (
                <div key={c.name} data-testid={`court-status-${c.name}`} className="glass rounded-lg px-2.5 py-1.5 flex items-center gap-2">
                  <Circle className="w-2.5 h-2.5 shrink-0 fill-current" style={{ color: c.in_use ? "#f59e0b" : "#22c55e" }} />
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-bold flex items-center gap-1.5"><MapPin className="w-3 h-3 text-primary-c shrink-0" /> {c.name}</div>
                    <div className="text-[10px] mono truncate" style={{ color: c.in_use ? "#f59e0b" : "#22c55e" }}>
                      {c.in_use ? `In use · ${c.match.a_name} vs ${c.match.b_name}` : "Free"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {overview && overview.ready_unassigned.length > 0 && (
              <p className="text-[11px] mt-2" style={{ color: "var(--text-2)" }} data-testid="ready-unassigned-count">
                {overview.ready_unassigned.length} ready match(es) waiting for a court.
              </p>
            )}
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* PIN codes */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--text-2)" }}><KeyRound className="w-3.5 h-3.5 text-primary-c" /> Court PIN codes</div>
          <div className="rounded-xl p-3 space-y-2 mb-3" style={{ background: "var(--bg)" }} data-testid="pin-form">
            <Select value={pinCat} onValueChange={(v) => { setPinCat(v); setPinMatch(""); }}>
              <SelectTrigger data-testid="pin-category" className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue placeholder="Category" /></SelectTrigger>
              <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                {cats.map((c) => <SelectItem key={c.id} value={c.id} data-testid={`pin-cat-${c.id}`}>{categoryLabel(c)}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={pinMatch} onValueChange={setPinMatch}>
              <SelectTrigger data-testid="pin-match" className="rounded-xl glow-border bg-surface" style={{ color: "var(--text)" }}><SelectValue placeholder={matches.length ? "Select match" : "No ready matches — generate a bracket first"} /></SelectTrigger>
              <SelectContent className="glass" style={{ background: "var(--surface-glass)" }}>
                {matches.map((m) => <SelectItem key={m.match_id} value={m.match_id} data-testid={`pin-match-${m.match_id}`}>{m.a_name} vs {m.b_name}</SelectItem>)}
              </SelectContent>
            </Select>
            <input data-testid="pin-label" value={pinLabel} onChange={(e) => setPinLabel(e.target.value)} placeholder="Label (e.g. Court 1)" className={`${inputCls} w-full`} style={{ color: "var(--text)" }} />
            <button data-testid="generate-pin" onClick={genPin} disabled={busy} className="btn-glow rounded-xl px-4 py-2.5 text-sm font-bold w-full flex items-center justify-center gap-2 disabled:opacity-60"><KeyRound className="w-4 h-4" /> Generate PIN</button>
          </div>
          <div className="space-y-2" data-testid="pin-list">
            {pins.length === 0 ? <p className="text-xs" style={{ color: "var(--text-2)" }}>No active PINs.</p> :
              pins.map((p) => (
                <div key={p.pin_id} data-testid={`pin-item-${p.pin_id}`} className="flex items-center justify-between glass rounded-xl px-3 py-2">
                  <div><div className="font-bold text-sm">{p.label}</div><div className="text-[10px] mono" style={{ color: "var(--text-2)" }}>starts {p.pin_prefix}•••• · expires {new Date(p.expires_at).toLocaleDateString()}</div></div>
                  <button data-testid={`revoke-pin-${p.pin_id}`} onClick={() => revokePin(p.pin_id)} className="w-8 h-8 rounded-full glass flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /></button>
                </div>
              ))}
          </div>
        </div>

        {/* accounts */}
        <div>
          <div className="text-xs font-bold uppercase tracking-wider mb-2 flex items-center gap-1.5" style={{ color: "var(--text-2)" }}><UserPlus className="w-3.5 h-3.5 text-primary-c" /> Umpire accounts</div>
          <div className="rounded-xl p-3 space-y-2 mb-3" style={{ background: "var(--bg)" }} data-testid="umpire-account-form">
            <input data-testid="umpire-name" value={acct.name} onChange={(e) => setAcct((a) => ({ ...a, name: e.target.value }))} placeholder="Umpire name" className={`${inputCls} w-full`} style={{ color: "var(--text)" }} />
            <input data-testid="umpire-email" value={acct.email} onChange={(e) => setAcct((a) => ({ ...a, email: e.target.value }))} placeholder="Umpire email" className={`${inputCls} w-full`} style={{ color: "var(--text)" }} />
            <button data-testid="create-umpire" onClick={createAccount} disabled={busy} className="btn-glow rounded-xl px-4 py-2.5 text-sm font-bold w-full flex items-center justify-center gap-2 disabled:opacity-60"><UserPlus className="w-4 h-4" /> Add umpire</button>
          </div>
          <div className="space-y-2" data-testid="umpire-list">
            {umpires.length === 0 ? <p className="text-xs" style={{ color: "var(--text-2)" }}>No umpire accounts yet.</p> :
              umpires.map((u) => (
                <div key={u.user_id} data-testid={`umpire-item-${u.user_id}`} className="flex items-center justify-between glass rounded-xl px-3 py-2">
                  <div><div className="font-bold text-sm">{u.name}</div><div className="text-[10px] mono" style={{ color: "var(--text-2)" }}>{u.email}</div></div>
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`edit-umpire-${u.user_id}`} onClick={() => setEditAcct({ user_id: u.user_id, name: u.name, email: u.email, password: "" })} className="w-8 h-8 rounded-full glass flex items-center justify-center"><Pencil className="w-3.5 h-3.5 text-primary-c" /></button>
                    <button data-testid={`remove-umpire-${u.user_id}`} onClick={() => removeUmpire(u.user_id)} className="w-8 h-8 rounded-full glass flex items-center justify-center"><X className="w-3.5 h-3.5" style={{ color: "#ef4444" }} /></button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* edit umpire account */}
      <Dialog open={!!editAcct} onOpenChange={(o) => !o && setEditAcct(null)}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="edit-umpire-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><Pencil className="w-5 h-5 text-primary-c" /> Edit umpire account</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>Update the umpire's name, login email, or set a new password.</DialogDescription></DialogHeader>
          {editAcct && (
            <div className="space-y-3">
              <input data-testid="edit-umpire-name" value={editAcct.name} onChange={(e) => setEditAcct((a) => ({ ...a, name: e.target.value }))} placeholder="Name" className="w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
              <input data-testid="edit-umpire-email" type="email" value={editAcct.email} onChange={(e) => setEditAcct((a) => ({ ...a, email: e.target.value }))} placeholder="Email" className="w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
              <input data-testid="edit-umpire-password" type="text" value={editAcct.password} onChange={(e) => setEditAcct((a) => ({ ...a, password: e.target.value }))} placeholder="New password (leave blank to keep)" className="w-full rounded-xl bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
            </div>
          )}
          <DialogFooter>
            <button data-testid="edit-umpire-cancel" onClick={() => setEditAcct(null)} className="glass rounded-full px-4 py-2 text-sm font-bold" style={{ color: "var(--text)" }}>Cancel</button>
            <button data-testid="edit-umpire-save" onClick={saveAcct} className="btn-glow rounded-full px-5 py-2 text-sm font-bold">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* new PIN reveal */}
      <Dialog open={!!newPin} onOpenChange={(o) => { if (!o) { setNewPin(null); setCopied(false); } }}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="pin-reveal">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><KeyRound className="w-5 h-5 text-primary-c" /> {newPin?.label} PIN</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>Give this one-time PIN to the umpire. They enter it at <b>/umpire</b> to open the scorecard. It won't be shown again.</DialogDescription></DialogHeader>
          <div className="flex items-center gap-2">
            <div data-testid="pin-value" className="flex-1 rounded-xl bg-surface glow-border px-4 py-4 text-center font-display font-black text-3xl tracking-[0.3em]">{newPin?.pin}</div>
            <button data-testid="copy-pin" onClick={copyPin} className="btn-glow rounded-xl px-4 py-4 font-bold">{copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}</button>
          </div>
        </DialogContent>
      </Dialog>

      {/* account creds reveal */}
      <Dialog open={!!creds} onOpenChange={(o) => !o && setCreds(null)}>
        <DialogContent className="glass" style={{ background: "var(--surface-glass)" }} data-testid="umpire-creds">
          <DialogHeader><DialogTitle className="font-display">Umpire account ready</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>{creds?.note}</DialogDescription></DialogHeader>
          <div className="rounded-xl p-4 space-y-2" style={{ background: "var(--bg)" }}>
            <div className="flex justify-between text-sm"><span style={{ color: "var(--text-2)" }}>Email</span><span className="mono font-bold" data-testid="creds-email">{creds?.email}</span></div>
            {creds?.temp_password && <div className="flex justify-between text-sm"><span style={{ color: "var(--text-2)" }}>Temp password</span><span className="mono font-bold" data-testid="creds-password">{creds.temp_password}</span></div>}
          </div>
          <p className="text-xs" style={{ color: "var(--text-2)" }}>They log in at the normal sign-in page, then open the Umpire Console.</p>
        </DialogContent>
      </Dialog>

      {/* audit log — per-match timeline */}
      <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
        <DialogContent className="glass max-h-[80vh] overflow-y-auto" style={{ background: "var(--surface-glass)" }} data-testid="audit-dialog">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><ScrollText className="w-5 h-5 text-primary-c" /> Match Audit Log</DialogTitle>
            <DialogDescription style={{ color: "var(--text-2)" }}>A permanent timeline of every point, fault and side-out — with the umpire and timestamp.</DialogDescription></DialogHeader>
          {audit === null ? <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-primary-c" /></div> :
            audit === false ? <p className="text-sm" style={{ color: "var(--text-2)" }}>Could not load.</p> :
            audit.length === 0 ? <p className="text-sm text-center py-6" style={{ color: "var(--text-2)" }}>No scoring activity yet.</p> :
            (() => {
              const groups = {};
              audit.forEach((a) => {
                const k = `${a.category_id}::${a.match_id}`;
                (groups[k] = groups[k] || []).push(a);
              });
              if (auditMatch && groups[auditMatch]) {
                const rows = [...groups[auditMatch]].sort((x, y) => (x.timestamp < y.timestamp ? -1 : 1));
                const first = rows[0];
                let prevServing = null;
                return (
                  <div data-testid="audit-timeline">
                    <button data-testid="audit-back" onClick={() => setAuditMatch(null)} className="text-xs flex items-center gap-1.5 mb-3" style={{ color: "var(--text-2)" }}><X className="w-3.5 h-3.5" /> All matches</button>
                    <div className="text-sm font-bold mb-1">{first.a_name || "—"} <span style={{ color: "var(--text-2)" }}>vs</span> {first.b_name || "—"}</div>
                    <div className="text-[10px] mono uppercase tracking-wider mb-3" style={{ color: "var(--text-2)" }}>{first.category_label}{first.round_label ? ` · ${first.round_label}` : ""} · {rows.length} events</div>
                    <div className="space-y-1.5">
                      {rows.map((a) => {
                        const sideOut = a.action === "FAULT" && prevServing !== null && a.serving_team !== prevServing;
                        prevServing = a.serving_team;
                        const meta = sideOut
                          ? { l: "SIDE OUT", c: "#f59e0b" }
                          : { POINT: { l: "POINT", c: "#22c55e" }, FAULT: { l: "FAULT", c: "#ef4444" }, UNDO: { l: "UNDO", c: "#a78bfa" }, SETUP: { l: "SETUP", c: "#06b6d4" }, finalize: { l: "FINAL", c: "#eab308" } }[a.action] || { l: a.action, c: "var(--text-2)" };
                        return (
                          <div key={a.audit_id} data-testid={`audit-event-${a.audit_id}`} className="flex items-center gap-3 text-xs glass rounded-lg px-3 py-2">
                            <span className="mono font-bold rounded-full px-2 py-0.5 shrink-0" style={{ color: meta.c, border: `1px solid ${meta.c}`, minWidth: 74, textAlign: "center" }}>{meta.l}</span>
                            <span className="font-display font-black">{a.a}–{a.b}</span>
                            <span className="mono" style={{ color: "var(--text-2)" }}>serve: {a.serving_team === "a" ? "A" : "B"}{a.server_number ? ` · #${a.server_number}` : ""}</span>
                            <span className="mono ml-auto truncate" style={{ color: "var(--text-2)" }}>{a.umpire_name} · {fmtTime(a.timestamp)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              }
              return (
                <div className="space-y-2" data-testid="audit-match-list">
                  {Object.entries(groups).sort((a, b) => (a[1][a[1].length - 1].timestamp < b[1][b[1].length - 1].timestamp ? 1 : -1)).map(([k, rows]) => {
                    const last = rows[rows.length - 1];
                    return (
                      <button key={k} data-testid={`audit-match-${k}`} onClick={() => setAuditMatch(k)}
                        className="w-full text-left glass rounded-xl px-4 py-3 flex items-center justify-between hover:scale-[1.005] transition-transform">
                        <div className="min-w-0">
                          <div className="font-bold text-sm truncate">{last.a_name || "—"} vs {last.b_name || "—"}</div>
                          <div className="text-[10px] mono uppercase tracking-wider" style={{ color: "var(--text-2)" }}>{last.category_label}{last.round_label ? ` · ${last.round_label}` : ""} · {rows.length} events · {last.a}–{last.b}</div>
                        </div>
                        <ScrollText className="w-4 h-4 text-primary-c shrink-0" />
                      </button>
                    );
                  })}
                </div>
              );
            })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
